
import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    TextField,
    Tooltip,
    Pagination,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Backdrop,
    CircularProgress,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import axios from "axios";
import base_url from "../../constants.js";
import Cookies from "js-cookie";
import { RefreshSharp } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import useNotification from "../common/Notification.js";
import CustomizedDrawer from "../Email/CustomizedDrawer.js";
import { blue } from "@mui/material/colors";

const getColor = (status) => {
    if (status === "PENDING") {
        return "#e6ba0b";
    } else if (status === "FAILED") {
        return "#e8120e";
    } else {
        return "#20bd6e";
    }
};

const options = ["FAILED", "COMPLETED", "PENDING", "READ", "DELIVERED"];

const MessageQueue = () => {
    const [data, setData] = useState([]);
    const [query, setQuery] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const [totalPage, setTotalPage] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [dateValue, setDateValue] = useState(null);
    const [statusOptions, setStatusOptions] = useState(null);
    const [backdropOpen, setBackdropOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [countData, setCountData] = useState({
        total_pending: 0,
        total_delivered: 0,
        total_read: 0,
        total_completed: 0,
        total_failed: 0,
    });
    const [personalCount, setPersonalCount] = useState(null);
    const [isFiltered, setIsFiltered] = useState(false);
    const [messageDialogOpen, setMessageDialogOpen] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState("");
    const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
    const [selectedMediaUrl, setSelectedMediaUrl] = useState("");
    const [selectedMediaType, setSelectedMediaType] = useState(null);

    const showNotification = useNotification();

    const fetchStatus = async (page = 1) => {
        const userId = Cookies.get("userId");
        setCurrentPage(page);

        try {
            setBackdropOpen(true);
            const res = await axios.get(
                `${base_url}/whatsapp/personal/whatsapp_queue/get/user/v1/${userId}?page=${page}`
            );
            setData(res.data.data.data);
            setFilteredData(res.data.data.data);
            setTotalPage(res.data.data.last_page);
            setCountData (res.data.summary);

            setTotalRows(res.data.data.total);
            setIsFiltered(false);
            showNotification("Latest data fetched!", "success");
        } catch (e) {
            console.log("Error: ", e);
            showNotification("Something went wrong while fetching!", "error");
        } finally {
            setBackdropOpen(false);
        }
    };

    useEffect(() => {
        fetchStatus();
    }, []);

    useEffect(() => {
        const userId = Cookies.get("userId");

        const fetchPersonalCount = async () => {
            try {
                const res = await axios.get(
                    `${base_url}/whatsapp/personal/get/user_count/${userId}`
                );
                setPersonalCount(res.data.data);
                if (res.data.data <= 0) {
                    setDrawerOpen(true);
                }
            } catch (e) {
                console.log("error : ", e);
            }
        };
        fetchPersonalCount();
    }, []);

    const handleQueryChange = (e) => {
        setQuery(e.target.value.trim());
    };

    const handleQuerySearch = async (page = currentPage) => {
        const userId = Cookies.get("userId");
        setBackdropOpen(true);
        try {
            const payload = {
                user_id: userId,
                date: dateValue,
                status: statusOptions,
                phone_number: query,
                page: page,
            };
            const res = await axios.post(
                `${base_url}/whatsapp/personal/whatsapp_queue/get/allusers/v1`,
                payload
            );
            setFilteredData(res.data.data.data);
            setTotalPage(res.data.data.last_page);
            setCountData (res.data.summary);
            setTotalRows(res.data.data.total);
            setIsFiltered(true);
            setCurrentPage(page);
            showNotification("Filtered Data!", "success");
        } catch (e) {
            console.log("Error: ", e);
            showNotification("No Data!", "error");
        } finally {
            setBackdropOpen(false);
        }
    };

    const handleReset = async () => {
        setQuery("");
        setStatusOptions(null);
        setDateValue(null);
        await fetchStatus(1);
    };

    const handlePageChange = async (event, value) => {
        if (isFiltered) {
            await handleQuerySearch(value);
        } else {
            await fetchStatus(value);
        }
    };

    const handleDateChange = (newValue) => {
        if (newValue instanceof Date && !isNaN(newValue)) {
            const formatted = format(newValue, "yyyy-MM-dd");
            setDateValue(formatted);
        } else {
            console.warn("Invalid date:", newValue);
        }
    };

    const handleStatusSelect = (e) => {
        setStatusOptions(e.target.value);
    };

    const handleOpenMessageDialog = (message) => {
        setSelectedMessage(message || "");
        setMessageDialogOpen(true);
    };

    const handleCloseMessageDialog = () => {
        setMessageDialogOpen(false);
        setSelectedMessage("");
    };

    const getMediaTypeFromUrl = (url) => {
        if (!url) return null;
        const cleanUrl = url.split("?")[0].toLowerCase();

        if (cleanUrl.match(/\.(jpg|jpeg|png|gif|webp)$/)) {
            return "image";
        }
        if (cleanUrl.match(/\.(mp4|webm|ogg|mov)$/)) {
            return "video";
        }
        if (cleanUrl.endsWith(".pdf")) {
            return "pdf";
        }
        return "file";
    };

    const handleOpenMediaDialog = (url) => {
        if (!url) return;

        const type = getMediaTypeFromUrl(url);

        // For PDFs or unknown files, trigger download / open in new tab
        if (type === "pdf" || type === "file") {
            window.open(url, "_blank", "noopener,noreferrer");
            return;
        }

        setSelectedMediaUrl(url);
        setSelectedMediaType(type);
        setMediaDialogOpen(true);
    };

    const handleCloseMediaDialog = () => {
        setMediaDialogOpen(false);
        setSelectedMediaUrl("");
        setSelectedMediaType(null);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
    };

    return (
        <Box sx={{ marginTop: "2rem" }}>
            <Typography variant='h6'  sx={{ mb: 4, mt: 3, color : 'primary.main', backgroundColor:blue[50], px:2, width: 'fit-content', py:1, borderRadius:2, fontWeight:'bold'}}>
                Whatsapp Message Queue
            </Typography>
            <Backdrop
                sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 1,
                })}
                open={backdropOpen}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <CustomizedDrawer
                message={`Personal Whatsapp credit has been exceeded.`}
                drawerOpen={drawerOpen}
                closeDrawer={closeDrawer}
                onCancel={() => setDrawerOpen(false)}
            />

            <Box sx={{ width: "100%", display: "flex" }}>
                <Box
                    sx={{
                        width: "auto",
                        height: "auto",
                        gap: 1,
                        display: "flex",
                        mb: 2,
                        marginRight: "auto",
                    }}
                >
                    <TextField
                        variant="outlined"
                        label="Enter Phone Number"
                        size="small"
                        value={query}
                        onChange={handleQueryChange}
                    />

                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Select a date"
                            value={dateValue}
                            onChange={handleDateChange}
                            renderInput={(params) => (
                                <TextField {...params} size="small" />
                            )}
                        />
                    </LocalizationProvider>

                    <FormControl sx={{ width: "12rem" }}>
                        <InputLabel size="small">Select status</InputLabel>
                        <Select
                            value={statusOptions}
                            label="Select an Option"
                            onChange={handleStatusSelect}
                            size="small"
                        >
                            {options.map((option, i) => (
                                <MenuItem key={i} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleQuerySearch(1)}
                    >
                        Search
                    </Button>

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleReset}
                    >
                        RESET
                    </Button>
                </Box>

                <Box sx={{ marginLeft: "auto", width: "auto" }}>
                    <Tooltip title="Refresh Table">
                        <Button
                            variant="contained"
                            onClick={() => fetchStatus(1)}
                        >
                            <RefreshSharp />
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            <Box
                sx={{
                    height: "2rem",
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Box sx={{ fontWeight: "500" }}>Total Rows: {totalRows}</Box>
                <Box sx={{ fontWeight: "500" }}>Pending: {countData.total_pending} | Delivered: {countData.total_delivered} | Read: {countData.total_read} | Failed: {countData.total_failed} </Box>

            </Box>



            <Paper sx={{ width: "100%", overflow: "hidden" }}>
                <TableContainer sx={{ maxHeight: "calc(100vh - 300px)" ,overflowY: "auto", }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        backgroundColor: "#1a2333", // Dark background for header row
                                        color: "#fff", // White text

                                        "& th": {
                                            // Target all header cells
                                            fontWeight: "bold !important",
                                            fontSize: "0.875rem",
                                            padding: "12px 16px",
                                            borderBottom: "none",
                                        },
                                    }}
                                >
                                    ID
                                </TableCell>
                                <TableCell
                                    sx={{
                                        backgroundColor: "#1a2333", // Dark background for header row
                                        color: "#fff", // White text

                                        "& th": {
                                            // Target all header cells
                                            fontWeight: "bold !important",
                                            fontSize: "0.875rem",
                                            padding: "12px 16px",
                                            borderBottom: "none",
                                        },
                                    }}
                                >
                                    PHONE NUMBER
                                </TableCell>
                                <TableCell
                                    sx={{
                                        backgroundColor: "#1a2333", // Dark background for header row
                                        color: "#fff", // White text

                                        "& th": {
                                            // Target all header cells
                                            fontWeight: "bold !important",
                                            fontSize: "0.875rem",
                                            padding: "12px 16px",
                                            borderBottom: "none",
                                        },
                                    }}
                                >
                                    MESSAGE CONTENT
                                </TableCell>
                                <TableCell
                                    sx={{
                                        backgroundColor: "#1a2333", // Dark background for header row
                                        color: "#fff", // White text

                                        "& th": {
                                            // Target all header cells
                                            fontWeight: "bold !important",
                                            fontSize: "0.875rem",
                                            padding: "12px 16px",
                                            borderBottom: "none",
                                        },
                                    }}
                                >
                                    ATTACHED MEDIA
                                </TableCell>
                                <TableCell
                                    sx={{
                                        backgroundColor: "#1a2333", // Dark background for header row
                                        color: "#fff", // White text

                                        "& th": {
                                            // Target all header cells
                                            fontWeight: "bold !important",
                                            fontSize: "0.875rem",
                                            padding: "12px 16px",
                                            borderBottom: "none",
                                        },
                                    }}
                                    align="center"
                                >
                                    MESSAGE PRIORITY
                                </TableCell>
                                <TableCell
                                    sx={{
                                        backgroundColor: "#1a2333", // Dark background for header row
                                        color: "#fff", // White text

                                        "& th": {
                                            // Target all header cells
                                            fontWeight: "bold !important",
                                            fontSize: "0.875rem",
                                            padding: "12px 16px",
                                            borderBottom: "none",
                                        },
                                    }}
                                >
                                    CREATED DATE
                                </TableCell>
                                  <TableCell
                                    sx={{
                                        backgroundColor: "#1a2333", // Dark background for header row
                                        color: "#fff", // White text

                                        "& th": {
                                            // Target all header cells
                                            fontWeight: "bold !important",
                                            fontSize: "0.875rem",
                                            padding: "12px 16px",
                                            borderBottom: "none",
                                        },
                                    }}
                                >
                                    LAST UPDATE
                                </TableCell>
                                <TableCell
                                    sx={{
                                        backgroundColor: "#1a2333", // Dark background for header row
                                        color: "#fff", // White text

                                        "& th": {
                                            // Target all header cells
                                            fontWeight: "bold !important",
                                            fontSize: "0.875rem",
                                            padding: "12px 16px",
                                            borderBottom: "none",
                                        },
                                    }}
                                    align="right"
                                >
                                    STATUS
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {(isFiltered ? filteredData : data)?.length > 0 ? (
                                (isFiltered ? filteredData : data).map(
                                    (row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.id}</TableCell>
                                            <TableCell>
                                                {row.phone_number}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip
                                                    title={
                                                        row.message
                                                            ? "Click to view full message"
                                                            : "No message content"
                                                    }
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            cursor: row.message
                                                                ? "pointer"
                                                                : "default",
                                                        }}
                                                        onClick={() =>
                                                            row.message &&
                                                            handleOpenMessageDialog(
                                                                row.message
                                                            )
                                                        }
                                                    >
                                                        {(row.message ||
                                                            "no message content"
                                                        ).slice(0, 22)}
                                                        {(row.message ||
                                                            "no message content"
                                                        ).length > 22
                                                            ? "..."
                                                            : null}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {row.image_url ? (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() =>
                                                            handleOpenMediaDialog(
                                                                row.image_url
                                                            )
                                                        }
                                                    >
                                                        View Media
                                                    </Button>
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                    >
                                                        No media attached
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {row.message_priority || "-"}
                                            </TableCell>
                                            <TableCell>
                                                {row.created_at?.slice(0, 10) ||
                                                    "null"}
                                            </TableCell>
                                            <TableCell>
                                                {row.updated_at?.slice(0, 10) ||
                                                    "-"}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box
                                                    sx={{
                                                        width: "100px",
                                                        textAlign: "center",
                                                        padding: "0.8rem",
                                                        color: "White",
                                                        borderRadius: 10,
                                                        bgcolor: getColor(
                                                            row.status
                                                        ),
                                                    }}
                                                >
                                                    {row.status}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )
                                )
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        No data found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog
                open={messageDialogOpen}
                onClose={handleCloseMessageDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Message Content</DialogTitle>
                <DialogContent dividers>
                    <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap" }}
                    >
                        {selectedMessage || "no message content"}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseMessageDialog}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={mediaDialogOpen}
                onClose={handleCloseMediaDialog}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>Attached Media</DialogTitle>
                <DialogContent
                    dividers
                    sx={{ display: "flex", justifyContent: "center" }}
                >
                    {selectedMediaType === "image" && selectedMediaUrl && (
                        <Box sx={{ textAlign: "center" }}>
                            <img
                                src={selectedMediaUrl}
                                alt="Attached media"
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "70vh",
                                }}
                            />
                        </Box>
                    )}
                    {selectedMediaType === "video" && selectedMediaUrl && (
                        <Box sx={{ textAlign: "center" }}>
                            <video
                                src={selectedMediaUrl}
                                controls
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "70vh",
                                }}
                            />
                        </Box>
                    )}
                    {!selectedMediaUrl && (
                        <Typography variant="body2">
                            No media available.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseMediaDialog}>Close</Button>
                </DialogActions>
            </Dialog>

            <Box
                sx={{
                    marginLeft: "auto",
                    width: "100%",
                    display: "flex",
                    justifyContent: "end",
                    mt: 2,
                }}
            >
                {totalPage > 1 && (
                    <Pagination
                        count={totalPage}
                        color="primary"
                        page={currentPage}
                        onChange={handlePageChange}
                    />
                )}
            </Box>
        </Box>
    );
};

export default MessageQueue;
